import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { AudiHeaderActiveBarProps } from '../../interfaces/header-components.interfaces';
import type { HeaderStyledActiveBarProps } from '../../interfaces/header-styled-components.interfaces';
import { createBreakpointStyles } from '../../services/index';
import { timeS } from '../AudiHeader/AudiHeaderStyleUtils';
import { responsiveStyles } from '../../utils/responsiveStyles';
import type { CustomTheme } from '../../../@types/theme';

const headerStyledActiveBarStyles = (
  theme: CustomTheme,
): Array<{ maxNavItems: number; style: unknown }> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      'background-color': {
        l: 'var(--one-header-color-black)',
      },
      display: { l: 'block' },
      height: { l: '2px' },
      left: { l: '0' },
      opacity: { l: '0' },
      position: { l: 'absolute' },
      top: {
        l: `/* corresponds to the top position of a navigation being 72px high */
          calc(2 * var(--one-header-space-l))`,
      },
      transition: {
        l: `left ${timeS} ${theme.easing.easingDefault},
          width ${timeS} ${theme.easing.easingDefault},
          opacity ${timeS}`,
      },
      width: { l: '0' },
    },
  },
  {
    maxNavItems: 5,
    style: {
      'background-color': {
        xl: 'var(--one-header-color-black)',
      },
      display: { xl: 'block' },
      height: { xl: '2px' },
      left: { xl: '0' },
      opacity: { xl: '0' },
      position: { xl: 'absolute' },
      top: {
        xl: `/* corresponds to the top position of a navigation being 72px high */
          calc(2 * var(--one-header-space-l))`,
      },
      transition: {
        xl: `left ${timeS} ${theme.easing.easingDefault},
        width ${timeS} ${theme.easing.easingDefault},
        opacity ${timeS}`,
      },
      width: { xl: '0' },
    },
  },
  {
    maxNavItems: 8,
    style: {
      'background-color': {
        xxl: 'var(--one-header-color-black)',
      },
      display: { xxl: 'block' },
      height: { xxl: '2px' },
      left: { xxl: '0' },
      opacity: { xxl: '0' },
      position: { xxl: 'absolute' },
      top: {
        xxl: `/* corresponds to the top position of a navigation being 72px high */
          calc(2 * var(--one-header-space-l))`,
      },
      transition: {
        xxl: `left ${timeS} ${theme.easing},
          width ${timeS} ${theme.easing},
          opacity ${timeS}`,
      },
      width: { xxl: '0' },
    },
  },
];

const HeaderStyledActiveBar = styled.div<HeaderStyledActiveBarProps>`
  ${({ headerNavigationItemsAmount, theme }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerStyledActiveBarStyles(theme)),
      theme.breakpoints,
    )};
`;

const AudiHeaderActiveBar: React.FC<AudiHeaderActiveBarProps> = ({
  headerNavigationItemsAmount,
  headerState,
  windowWidth,
}) => {
  const activeBarStyles = useMemo(() => {
    const currentActiveAnchor = headerState.activeItem.anchor;
    let cssProperties = {
      left: 0,
      opacity: 0,
      width: 0,
    };
    if (currentActiveAnchor) {
      cssProperties = {
        left: currentActiveAnchor.offsetLeft,
        opacity: 1,
        width: currentActiveAnchor.clientWidth,
      };
    }

    return cssProperties;
  }, [headerState.activeItem.anchor, windowWidth]);

  return (
    <HeaderStyledActiveBar
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      style={activeBarStyles}
    />
  );
};

export default AudiHeaderActiveBar;
